import React, { useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import Grid from '@material-ui/core/Grid';
import { v1 as uuidv1 } from 'uuid';
import DataForm from 'forms/DataForm';
import { request } from 'utilities/graph';
import {
  createReportFile,
  updateReportFile,
} from 'graphql/mutations';
import uiSchema from './uiSchema';
import FileUploader from './FileUploader';
import cache from 'utilities/cache';
import { toastr } from 'react-redux-toastr';
import moment from 'moment-timezone';
import { useCache } from 'CacheProvider';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';

export default function ReportFileForm({ formData: inFormData, onComplete, organizations, ...props }) {
  // load here for translation purpose
  const { default: schema } = useMemo(() => require('./schema.js'), []);
  const [currentFileS3Key, setCurrentFileS3Key] = useState();
  const [newFileS3Key, setNewFileS3Key] = useState();
  const [agreed, setAgreed] = useState(false);
  const { selectedUserGroupParams } = useCache();
  const facilityId = cache.get('app:facilityId');

  const createFunc = async (data) => {
    if (!newFileS3Key) {
      toastr.error('請選擇要上傳的檔案');
      throw new Error('abort');
    }

    if (!agreed) {
      toastr.error('請閱讀並同意上傳報表同意須知');
      throw new Error('abort');
    }

    data.id = uuidv1();
    data.facilityId = facilityId;
    data.fileS3Key = newFileS3Key;

    await request(createReportFile, { input: data });

    await onComplete(data);
  };

  const updateFunc = async (data) => {
    if (!newFileS3Key) {
      toastr.error('請選擇要上傳的檔案');
      throw new Error('abort');
    }

    if (!agreed) {
      toastr.error('請閱讀並同意上傳報表同意須知');
      throw new Error('abort');
    }

    data.fileS3Key = newFileS3Key;

    await request(updateReportFile, { input: data });

    await onComplete(data);
  };

  useEffect(() => {
    if (inFormData && inFormData.id) {
      setCurrentFileS3Key(inFormData.fileS3Key);
      setNewFileS3Key(inFormData.fileS3Key);
    }
  }, [inFormData]);

  const now = moment().toISOString();

  return (
    <div>
      <DataForm
        schema={schema}
        uiSchema={uiSchema}
        createFunc={createFunc}
        updateFunc={updateFunc}
        formData={inFormData}
        dirty={true}
        extMappings={[{
          key: 'organizationId',
          func: () => {
            const organizationIdToUploadReport = selectedUserGroupParams.clientData?.featureConfiguration?.organizationIdToUploadReport;
            if (organizationIdToUploadReport) {
              return {
                enum: organizations.filter(({ id }) => id === organizationIdToUploadReport).map(({ id }) => id),
                enumNames: organizations.filter(({ id }) => id === organizationIdToUploadReport).map(({ name }) => name),
                default: organizationIdToUploadReport,
              };
            } else {
              console.error('no organizationIdToUploadReport');
            }
            return undefined;
          },
        }]}
        submitButtonTitle="確認上傳"
        {...props}
      >
        <Grid container spacing={2} style={{ paddingTop: 16 }}>
          <FileUploader
            s3Prefix={`reportFiles/${facilityId}/${now}`}
            s3Key={currentFileS3Key}
            onUpdateS3Key={setNewFileS3Key}
          />
          <Grid item xs={12}>
            <Box
              border={1}
              borderColor="grey.300"
              borderRadius={5}
              mt={2}
              mb={2}
              p={2}
            >
              <Typography variant="body1" gutterBottom>
                請您在上傳報表前，仔細閱讀並同意以下內容：
              </Typography>
              <Typography variant="body2" style={{ lineHeight: 1.8, marginBottom: 16 }}>
                <ul style={{ paddingLeft: 20, margin: 0 }}>
                  <li>本平台僅提供報表上傳及存取功能，供所屬組織進行查看及管理，並不對報表內容進行審核、編輯或認證。</li>
                  <li>報表內容由各上傳單位（即本機構）自行編製與上傳，其正確性、完整性及合法性由上傳單位自行負責。</li>
                  <li>上傳之報表一經提交，將可供所屬組織檢視，請確認內容無誤後再行上傳。</li>
                  <li>因報表內容所引發之任何爭議、誤解或法律責任，均由上傳單位自行承擔，本平台不負任何法律責任。</li>
                  <li>上傳即表示您已閱讀、理解並同意以上條款。</li>
                </ul>
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={agreed}
                    onChange={(e) => setAgreed(e.target.checked)}
                    color="primary"
                  />
                }
                label="我已閱讀並同意以上內容"
              />
            </Box>
          </Grid>
        </Grid>
      </DataForm>
    </div>
  );
}

ReportFileForm.propTypes = {
  data: PropTypes.object,
  formData: PropTypes.object,
  onComplete: PropTypes.func,
  organizations: PropTypes.array,
};
